import type { QueueItem } from "@/types/clinician";

export type InboxFilter =
  | "nursing"
  | "prescriber"
  | "needs"
  | "urgent"
  | "overdue"
  | "unassigned"
  | "waiting"
  | "sent"
  | "snoozed"
  | "older"
  | "inactive"
  | "resolved"
  | "automated"
  | "all";

export type InboxSort = "smart" | "oldest" | "newest";

export type InboxScope = {
  filter: InboxFilter;
  query?: string;
  category?: string;
  /** Structured AI tag keys. Multiple selected tags match any selected key. */
  tags?: string[];
  ageHours?: string;
  ownerId?: string;
  sort?: InboxSort;
};

const ACTIVE_STATES = new Set(["new", "ready", "in_progress"]);
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export function isOlderThanThirtyDays(
  item: QueueItem,
  now = Date.now(),
): boolean {
  if (!item.last_patient_message_at) return false;
  const timestamp = new Date(item.last_patient_message_at).getTime();
  return Number.isFinite(timestamp) && now - timestamp >= THIRTY_DAYS_MS;
}

function isCurrentActivePlanWork(item: QueueItem, now: number): boolean {
  return (
    item.subscription_state !== "ended" && !isOlderThanThirtyDays(item, now)
  );
}

export function isSentToday(item: QueueItem, now = Date.now()): boolean {
  if (!item.last_clinician_reply_at) return false;
  const sentAt = new Date(item.last_clinician_reply_at).getTime();
  if (!Number.isFinite(sentAt)) return false;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  return sentAt >= start.getTime() && sentAt <= now;
}

export function matchesInboxFilter(
  item: QueueItem,
  filter: InboxFilter,
  now = Date.now(),
): boolean {
  const activeWork =
    item.needs_clinician && ACTIVE_STATES.has(item.workflow_state);
  switch (filter) {
    case "nursing":
      return (
        activeWork &&
        isCurrentActivePlanWork(item, now) &&
        (item.required_capability === "nursing" ||
          item.required_capability === "any")
      );
    case "prescriber":
      return (
        activeWork &&
        isCurrentActivePlanWork(item, now) &&
        (item.required_capability === "prescriber" ||
          item.required_capability === "any")
      );
    case "needs":
      return activeWork && isCurrentActivePlanWork(item, now);
    case "urgent":
      return item.needs_clinician && item.urgency === 3;
    case "overdue":
      return item.needs_clinician && item.sla_breached;
    case "unassigned":
      return (
        activeWork && isCurrentActivePlanWork(item, now) && !item.assigned_to
      );
    case "waiting":
      return (
        item.workflow_state === "waiting_patient" ||
        item.workflow_state === "waiting_internal"
      );
    case "sent":
      return isSentToday(item, now);
    case "snoozed":
      return item.workflow_state === "snoozed";
    case "older":
      return (
        activeWork &&
        item.subscription_state !== "ended" &&
        isOlderThanThirtyDays(item, now)
      );
    case "inactive":
      return activeWork && item.subscription_state === "ended";
    case "resolved":
      return (
        item.workflow_state === "resolved" || item.workflow_state === "closed"
      );
    case "automated":
      return item.resolution_reason === "automated_handled";
    case "all":
      return true;
  }
}

export function matchesInboxSearch(item: QueueItem, query = ""): boolean {
  const normalized = query.trim().toLocaleLowerCase();
  if (!normalized) return true;
  return [
    item.patient.display_name,
    item.patient.email,
    item.last_patient_message_preview,
    item.category,
    item.urgency_reason,
    ...(item.priority_signals ?? []),
    ...(item.tags ?? []),
    item.workflow_state,
    item.resolution_reason,
    item.assigned_to_email,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLocaleLowerCase().includes(normalized));
}

function normalizeTagKey(value: string) {
  return value.trim().toLocaleLowerCase().replaceAll("-", "_");
}

export function matchesInboxTag(item: QueueItem, tagKey: string): boolean {
  const normalized = normalizeTagKey(tagKey);
  if (!normalized) return false;
  if (normalized === "safety" && item.urgency === 3) return true;
  if (normalized === "reopened_after_reply" && item.reopened) return true;
  return (item.tags ?? []).some(
    (itemTag) => normalizeTagKey(String(itemTag)) === normalized,
  );
}

export function matchesInboxScope(
  item: QueueItem,
  scope: InboxScope,
  now = Date.now(),
): boolean {
  if (!matchesInboxFilter(item, scope.filter, now)) return false;
  if (!matchesInboxSearch(item, scope.query)) return false;
  if (scope.category && item.category !== scope.category) return false;
  if (
    scope.tags?.length &&
    !scope.tags.some((tagKey) => matchesInboxTag(item, tagKey))
  )
    return false;
  if (
    scope.ownerId &&
    item.patient_owner?.clinician_id !== scope.ownerId &&
    item.work_owner?.clinician_id !== scope.ownerId
  )
    return false;
  if (scope.ageHours) {
    const timestamp = item.last_patient_message_at
      ? new Date(item.last_patient_message_at).getTime()
      : 0;
    if (!timestamp || now - timestamp > Number(scope.ageHours) * 60 * 60 * 1000)
      return false;
  }
  return true;
}

export function sortInboxItems(
  items: QueueItem[],
  sort: InboxSort = "smart",
  filter?: InboxFilter,
): QueueItem[] {
  if (sort === "smart") return items;
  const timestamp = (item: QueueItem) => {
    const value =
      filter === "sent"
        ? item.last_clinician_reply_at
        : item.last_patient_message_at;
    const parsed = value ? new Date(value).getTime() : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return [...items].sort((a, b) =>
    sort === "oldest"
      ? timestamp(a) - timestamp(b)
      : timestamp(b) - timestamp(a),
  );
}

export function scopedInboxItems(
  items: QueueItem[],
  scope: InboxScope,
  now = Date.now(),
): QueueItem[] {
  return sortInboxItems(
    items.filter((item) => matchesInboxScope(item, scope, now)),
    scope.sort,
    scope.filter,
  );
}
